/**
 * Utilitaires pour le calcul de la TVA par catégorie de service
 */

/**
 * Taux de TVA par catégorie de service selon les règles de Maison Eridia
 */
export const VAT_RATES_BY_CATEGORY = {
  // Toutes les catégories : 20% (entreprise d'organisation d'évènements)
  'traiteur': 0.20,
  'catering': 0.20,
  'lieux': 0.20,
  'lieux_evenements': 0.20,
  'venue': 0.20,
  'personnel': 0.20,
  'personnel_evenementiel': 0.20,
  'staff': 0.20,
  'decoration': 0.20,
  'décoration': 0.20,
  'mobilier': 0.20,
  'furniture': 0.20,
  'animation': 0.20,
  'confort': 0.20,
  'son_et_lumiere': 0.20,
  'son-et-lumiere': 0.20,
  'son_lumiere': 0.20,
  'sound_light': 0.20,
  
  // Défaut : 20%
  'default': 0.20
} as const;

/**
 * Détermine le taux de TVA pour une catégorie de service
 * @param category - La catégorie du service (slug ou nom)
 * @param categoryName - Le nom de la catégorie (optionnel, utilisé comme fallback)
 * @returns Le taux de TVA (0.10 pour 10%, 0.20 pour 20%)
 */
export function getVATRateForCategory(category?: string, categoryName?: string): number {
  if (!category && !categoryName) {
    return VAT_RATES_BY_CATEGORY.default;
  }

  // Normaliser la catégorie pour la recherche
  const normalizedCategory = (category || categoryName || '').toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[éèê]/g, 'e')
    .replace(/[àâ]/g, 'a');

  // Recherche directe dans les taux définis
  if (normalizedCategory in VAT_RATES_BY_CATEGORY) {
    return VAT_RATES_BY_CATEGORY[normalizedCategory as keyof typeof VAT_RATES_BY_CATEGORY];
  }

  // Recherche par mots-clés pour les cas non exacts
  if (normalizedCategory.includes('personnel') ||
      normalizedCategory.includes('staff') ||
      normalizedCategory.includes('service')) {
    return VAT_RATES_BY_CATEGORY.personnel;
  }

  if (normalizedCategory.includes('decoration') || 
      normalizedCategory.includes('decor')) {
    return VAT_RATES_BY_CATEGORY.decoration;
  }

  if (normalizedCategory.includes('mobilier') || 
      normalizedCategory.includes('furniture') ||
      normalizedCategory.includes('meuble')) {
    return VAT_RATES_BY_CATEGORY.mobilier;
  }

  if (normalizedCategory.includes('animation') || 
      normalizedCategory.includes('divertissement')) {
    return VAT_RATES_BY_CATEGORY.animation;
  }

  if (normalizedCategory.includes('confort') || 
      normalizedCategory.includes('comfort')) {
    return VAT_RATES_BY_CATEGORY.confort;
  }

  if (normalizedCategory.includes('son') || 
      normalizedCategory.includes('lumiere') ||
      normalizedCategory.includes('sound') ||
      normalizedCategory.includes('light') ||
      normalizedCategory.includes('audio') ||
      normalizedCategory.includes('eclairage')) {
    return VAT_RATES_BY_CATEGORY.son_et_lumiere;
  }

  if (normalizedCategory.includes('lieu') || 
      normalizedCategory.includes('venue') ||
      normalizedCategory.includes('espace')) {
    return VAT_RATES_BY_CATEGORY.lieux;
  }

  // Défaut : 20%
  return VAT_RATES_BY_CATEGORY.default;
}

/**
 * Calcule le montant de TVA pour un service
 * @param priceHT - Prix HT du service
 * @param category - Catégorie du service
 * @param categoryName - Nom de la catégorie (optionnel)
 * @returns Le montant de TVA
 */
export function calculateVATForService(priceHT: number, category?: string, categoryName?: string): number {
  const vatRate = getVATRateForCategory(category, categoryName);
  return priceHT * vatRate;
}

/**
 * Calcule le prix TTC pour un service
 * @param priceHT - Prix HT du service
 * @param category - Catégorie du service
 * @param categoryName - Nom de la catégorie (optionnel)
 * @returns Le prix TTC
 */
export function calculateTTCForService(priceHT: number, category?: string, categoryName?: string): number {
  const vatAmount = calculateVATForService(priceHT, category, categoryName);
  return priceHT + vatAmount;
}

/**
 * Formate le taux de TVA pour l'affichage
 * @param category - Catégorie du service
 * @param categoryName - Nom de la catégorie (optionnel)
 * @returns Le taux formaté (ex: "10%" ou "20%")
 */
export function formatVATRate(category?: string, categoryName?: string): string {
  const rate = getVATRateForCategory(category, categoryName);
  return `${Math.round(rate * 100)}%`;
}